import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {CheckResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/CheckResponse';
import {Design, IDesignInfo} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {VerifyEmailStep} from '@mintdata-wsp/common/state/UIState';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {IConfirmDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/ConfirmDialog';
import {BasePage} from '@mintdata-wsp/wsp/ui/pages/BasePage';
import {ApplicationsPage} from '@mintdata-wsp/wsp/ui/pages/workspace/wspPages/ApplicationsPage';
import {DesignsPage} from '@mintdata-wsp/wsp/ui/pages/workspace/wspPages/DesignsPage';
import {HomePage} from '@mintdata-wsp/wsp/ui/pages/workspace/wspPages/HomePage';
import {SearchPage} from '@mintdata-wsp/wsp/ui/pages/workspace/wspPages/SearchPage';
import {SettingsPage} from '@mintdata-wsp/wsp/ui/pages/workspace/wspPages/SettingsPage';
import {SpacesPage} from '@mintdata-wsp/wsp/ui/pages/workspace/wspPages/SpacesPage';
import {TemplatePage} from '@mintdata-wsp/wsp/ui/pages/workspace/wspPages/TemplatePage';
import {TemplatesPage} from '@mintdata-wsp/wsp/ui/pages/workspace/wspPages/TemplatesPage';
import {Wsp404Page} from '@mintdata-wsp/wsp/ui/pages/workspace/wspPages/Wsp404Page';
import {RouteUtil} from '@mintdata-wsp/wsp/ui/routes/RouteUtil';
import {paths} from '@mintdata-wsp/wsp/ui/routes/Routing';
import {WspSidebar} from '@mintdata-wsp/wsp/ui/views/workspace/home/sidebar/WspSidebar';
import {WspSidebarOverlay} from '@mintdata-wsp/wsp/ui/views/workspace/home/sidebar/WspSidebarOverlay';
import {WspTopbar} from '@mintdata-wsp/wsp/ui/views/workspace/home/topbar/WspTopbar';
import * as classnames from 'classnames/bind';
import {computed, reaction} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {Route, RouteComponentProps, Switch} from 'react-router';

const styles = require('./WspPage.less');
const cx = classnames.bind(styles);

@observer
export class WspPage extends BasePage<RouteComponentProps<any>, any>
{
    private prevLocation = '';

    private EMAIL_VERIFICATON_CODE_PARAM_NAME:string = 'emailVerificationCode';

    @computed
    private get _currentDesigns():Array<Design>
    {
        const {currentDesigns} = rootState.ui.mainPage;

        if( !currentDesigns ) return [];

        return currentDesigns.slice().sort((a, b) =>
        {
            const orderCmp = b.orderIndex - a.orderIndex;
            if( orderCmp !== 0 ) return orderCmp;
            return b.id - a.id;
        });
    }

    static openDesignOnMobileDialog = (designName:string) =>
    {
        const props:IConfirmDialogProps = {
            title: 'To Edit An Application',
            content: (
                <Column className={styles.openOnMobile}>
                    <div>
                        Please open the <b>{designName}</b> design in a desktop browser.
                    </div>
                    <br />
                    On this device, you can always read the documentation.
                </Column>
            ),
            buttonSet: [null, 'Read documentation'],
            onConfirmed: () =>
            {
                window.open('https://mintdata.com/docs/learn/');
            },
        };

        controllers.ui.dialogs.showDialog(Dialogs.Confirm, false, props);
    };

    private _checkIfMobileRedirect = () =>
    {
        let {currentRouteProps} = rootState.navigation;

        const {location: {state}} = currentRouteProps;
        if( state && state.match )
        {
            let designName:string = '';
            const {designId} = state.match.params;

            if( designId )
            {
                const design:IDesignInfo = controllers.ui.mainPage.getDesignById(designId);
                if( design )
                    designName = design.name;
            }

            WspPage.openDesignOnMobileDialog(designName);

            // TODO: ?
            // controllers.navigation.clearCurrentHistoryState();
        }
    };

    private _verifyEmail(emailVerificationCode:string):void
    {
        const {verifyEmail} = rootState.ui;

        verifyEmail.setStep(VerifyEmailStep.IN_PROGRESS_VERIFY_EMAIL);

        controllers.ui.dialogs.showDialog(Dialogs.EmailVerification, true);

        controllers.user.verifyEmail(emailVerificationCode).then((response) =>
        {
            if( response instanceof CheckResponse && !response.allowed )
            {
                verifyEmail.setStep(VerifyEmailStep.FAILED);
            }
            else
            {
                verifyEmail.setStep(VerifyEmailStep.VERIFIED);

                // to reset modal state
                controllers.ui.dialogs.hideDialog(Dialogs.EmailVerification);
                controllers.ui.dialogs.showDialog(Dialogs.EmailVerification, false, null, false);
            }

            RouteUtil.removeQueryVariable(this.EMAIL_VERIFICATON_CODE_PARAM_NAME);
        });
    }

    private _checkEmailVerificationStatus():void
    {
        const {verifyEmail} = rootState.ui;

        controllers.user.syncUserId().then(() =>
        {
            if( !rootState.userSession.currentUser.isEmailVerified )
            {
                verifyEmail.setStep(VerifyEmailStep.NEEDS_VERIFICATION);
                controllers.ui.dialogs.showDialog(Dialogs.EmailVerification, true);
            }
            else
            {
                verifyEmail.setStep(VerifyEmailStep.VERIFIED);
            }
        });
    }

    componentWillMount():void
    {
        // https://github.com/typekit/webfontloader
        (function(d)
        {
            var wf = d.createElement('script'),
                s = d.scripts[0];
            wf.src = 'https://ajax.googleapis.com/ajax/libs/webfont/1.6.26/webfont.js';
            wf.async = true;
            s.parentNode.insertBefore(wf, s);

            d.body.style.fontFamily = `'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`;
        })(document);
    }

    componentDidMount()
    {
        const {match} = this.props;
        controllers.ui.mainPage.setLocationMatch(match);

        controllers.ui.mainPage.init();

        // controllers.ui.anyPage.disablePageLoader();

        reaction(
            () => rootState.ui.mainPage.isDesignsLoading,
            () =>
            {
                this._checkIfMobileRedirect();
            }
        );

        const emailVerificationCode = RouteUtil.getQueryVariable(this.EMAIL_VERIFICATON_CODE_PARAM_NAME);

        if( emailVerificationCode )
        {
            this._verifyEmail(emailVerificationCode);
        }
        else
        {
            this._checkEmailVerificationStatus();
        }
    }

    componentDidUpdate(prevProps:Readonly<RouteComponentProps<any>>, prevState:Readonly<any>, prevContext:any):void
    {
        const {match} = this.props;
        controllers.ui.mainPage.setLocationMatch(match);

        const location = this.props.location.pathname;

        // skip updates with same props
        if( this.prevLocation === location ) return;

        // first event fired in componentDidMount -> controllers.ui.mainPage.init()
        // skip first
        if( this.prevLocation )
        {
            controllers.analytics.main.trackEvent(AnalyticsEventActions.WSP_PAGE_LOADED);
        }

        this.prevLocation = location;
    }

    render():JSX.Element
    {
        const {match} = this.props;

        return (
            <Row className={cx(styles.page)}>

                <Column className={cx(styles.mainArea)}>

                    <WspTopbar match={match} />

                    <Row className={cx(styles.content)}>

                        <WspSidebar />
                        <WspSidebarOverlay />

                        <Column className={cx(styles.pageBody)}>

                            <Switch>
                                <Route exact path={`${paths.index()}`} component={HomePage} />
                                <Route exact path={`${paths.search()}`} component={SearchPage} />
                                <Route exact path={`${paths.designs()}`} component={DesignsPage} />
                                <Route exact path={`${paths.applications()}`} component={ApplicationsPage} />

                                <Route exact path={`${paths.templates()}`} component={TemplatesPage} />
                                <Route exact path={`${paths.template(':templateName')}`} component={TemplatePage} />

                                <Route path={`${paths.spaces(':projectId')}`} component={SpacesPage} />
                                <Route path={`${paths.settings()}`} component={SettingsPage} />

                                <Route path="*" component={Wsp404Page} />
                            </Switch>

                        </Column>

                    </Row>
                </Column>

                {/*<WspDocs />*/}
                {/*<LearnExperience />*/}

            </Row>
        );
    }
}
